import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit';

import { LD_CLAIM_DUPLICATE_STATUS, LD_CLAIM_DUPLICATE_STATUS_MESSAGE } from '../../../../../../contants/ClaimsEnums';
import { routeFileSelector, valueToString } from '../../../../../../utilz';
import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import SaveCancelButton from '../../../../../components/Common/SaveCancelButton';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import ConfirmationModal from '../../../../../components/Modals/ConfirmationModal';
import { useCheckDuplicateClaim, useGetClaimDetails, useSaveClaim } from '../../hooks';
import LADClaimDetailsForm from './LADClaimDetailsForm';

const LADClaimsDetailsTab = ({ claimKey }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  const { data: claimDetails } = useGetClaimDetails(claimKey);
  const { mutate: saveClaim } = useSaveClaim({
    onSuccess: () => {
      setEditMode(false);
    },
  });
  const { mutate: checkDuplicate } = useCheckDuplicateClaim({ showLoader: false });
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const claimFormDataRef = useRef(null);
  const invoiceAndFbNoRef = useRef({
    claimProNo: undefined,
    invoiceNo: undefined,
  });

  useEffect(() => {
    if (claimDetails?.claimNumber) {
      form.setFieldsValue(claimDetails);
    }
  }, [claimDetails, form]);

  const handleCancel = () => {
    if (claimDetails) {
      form.setFieldsValue(claimDetails);
    }
    setEditMode(false);
  };

  const handleSubmit = async (data) => {
    try {
      if (!claimDetails) {
        return;
      }
      let payload = cloneDeep(claimDetails);
      payload = { ...payload, ...data };
      payload = LADCovertDatesFromMomentToAPIType(payload);

      checkDuplicate(
        {
          claimKey: claimDetails.claimKey,
          freightBillNo: valueToString(payload.claimProNo),
          invoiceNo: valueToString(payload.invoiceNo),
        },
        {
          onSuccess: (duplicateStatus) => {
            if (Number(duplicateStatus) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
              saveClaim(payload);
              return;
            }
            setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
            claimFormDataRef.current = payload;
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDuplicateApprove = () => {
    if (!isInstantDuplicateValidation && claimFormDataRef.current) {
      saveClaim(claimFormDataRef.current);
    }
    handleDuplicateCancel();
  };

  const handleDuplicateCancel = () => {
    setDuplicateMessage(false);
    setIsInstantDuplicateValidation(false);
    claimFormDataRef.current = null;
  };

  const checkInstantClaimsDuplicate = (changedFieldName) => async () => {
    try {
      if (!claimDetails) {
        return;
      }
      const basicDetails = form.getFieldsValue();
      if (invoiceAndFbNoRef.current[changedFieldName] === basicDetails[changedFieldName]) {
        return;
      }
      invoiceAndFbNoRef.current = {
        claimProNo: basicDetails.claimProNo,
        invoiceNo: basicDetails.invoiceNo,
      };
      checkDuplicate(
        {
          claimKey: claimDetails.claimKey,
          freightBillNo: valueToString(basicDetails.claimProNo),
          invoiceNo: valueToString(basicDetails.invoiceNo),
        },
        {
          onSuccess: (duplicateStatus) => {
            if (Number(duplicateStatus) !== LD_CLAIM_DUPLICATE_STATUS.NEW) {
              setIsInstantDuplicateValidation(true);
              setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
            } else {
              setIsInstantDuplicateValidation(false);
            }
          },
        },
      );
    } catch (error) {
      console.log(error);
      setIsInstantDuplicateValidation(false);
    }
  };

  return (
    <>
      <Form onFinish={handleSubmit} className="h-100 d-flex flex-direction-column" form={form}>
        <div className="py-10 px-30">
          <Row justify="space-between" align="middle">
            <Col>
              <PrimaryLabel>Details</PrimaryLabel>
            </Col>
            <Col>
              {!editMode ? (
                <Button onClick={() => setEditMode(true)}>Edit</Button>
              ) : (
                <SaveCancelButton editMode onCancel={handleCancel} />
              )}
            </Col>
          </Row>
        </div>
        <div>
          <Divider className="m-0 border-bottom-border" />
        </div>
        <div className="px-30 py-10 flex-1 custom-scroll-bar">
          <LADClaimDetailsForm
            editMode={editMode}
            form={form}
            preparedBy={claimDetails?.createdByUser}
            checkInstantClaimsDuplicate={checkInstantClaimsDuplicate}
          />
        </div>
      </Form>
      <ConfirmationModal
        open={Boolean(duplicateMessage)}
        onClose={handleDuplicateCancel}
        onOk={handleDuplicateApprove}
        onCancel={handleDuplicateCancel}
        description={duplicateMessage}
        title={t('translation:default.fields.alert')}
        cancelButton={isInstantDuplicateValidation}
      />
    </>
  );
};

export default LADClaimsDetailsTab;
